import { createSelector } from '@reduxjs/toolkit';
import { get, orderBy, find, pick, isEmpty, isNumber } from 'lodash/fp';
import { MODEL_DTO_TYPE } from 'app/store/__generated__/types';
import { Defaults, ErrorLevel } from 'app/lib/constants';
import { isWorkbench } from 'app/routes/routes.js';
import { selectEntitiesById } from 'app/store/entities/selectors.js';
import { selectLastJobRun, selectJobRun } from 'app/store/jobs/selectors';
import { selectView } from 'app/store/view/selectors.js';
import { getTagsBySpaceUid, getRunChecksResults } from './utils.js';
import { ConversionStatus } from './constants.js';

export const selectSlice = get('projectModels');

export const selectIsCreateJobDialogOpen = createSelector(
    selectSlice,
    get('isCreateJobDialogOpen')
);
export const selectIsDeployModelDialogOpen = createSelector(
    selectSlice,
    get('deployModelDialog.isOpen')
);
const selectEntityId = createSelector(selectSlice, get('deployModelDialog.entityId'));
export const selectViewToDeploy = createSelector(
    selectEntitiesById,
    selectEntityId,
    (entitiesById, entityId) => entitiesById?.[entityId]
);
export const selectIsRedeploying = createSelector(
    selectViewToDeploy,
    (view) => view?._type === MODEL_DTO_TYPE
);

export const selectLastCreatedTag = createSelector(selectSlice, get('lastCreatedTag'));
const selectModels = createSelector(selectSlice, get('models'));

export const selectValidatedJobList = createSelector(selectSlice, get('validatedJobs.data'));
export const selectIsLoadingJobList = createSelector(selectSlice, get('validatedJobs.isLoading'));

const selectTags = createSelector(selectSlice, get('tags.data'));
export const selectIsLoadingTags = createSelector(selectSlice, get('tags.isLoading'));

export const selectModelJobLastRunStatuses = createSelector(selectSlice, get('jobLastRunStatuses'));
const selectPropEntityId = (_, props) => props.entityId;

const selectAllTagsByProjectId = createSelector(selectTags, getTagsBySpaceUid);

export const selectProjectsContainingTags = createSelector(selectAllTagsByProjectId, (tags) => {
    const projects = [];
    for (const projectId in tags) {
        projects.push({
            id: tags[projectId].projectId,
            name: tags[projectId].name,
            nodeId: tags[projectId].nodeId
        });
    }
    return projects;
});

const selectPropProjectId = (_, props) => props.projectId;
const selectPropNodeId = (_, props) => props.nodeId;
const selectPropTagId = (_, props) => props.tagId;

export const selectTagForPropTagId = createSelector(selectTags, selectPropTagId, (tags, tagId) => {
    const tag = find({ tagId }, tags);
    return pick(['projectId', 'tagId', 'modelId', 'tagName', 'projectVersion'], tag);
});

export const selectModelsForPropProjectId = createSelector(
    selectPropProjectId,
    selectAllTagsByProjectId,
    (projectId, tags) => {
        if (isEmpty(tags) || !projectId || isEmpty(tags[projectId])) {
            return [];
        }
        const nodes = tags[projectId].nodes;
        const models = [];
        for (const nodeId in nodes) {
            models.push({
                nodeId: nodes[nodeId].nodeId,
                nodeName: nodes[nodeId].nodeName
            });
        }

        return models;
    }
);

function getTagsForProjectAndNode(projectId, nodeId, tags) {
    if (isEmpty(tags) || !projectId || !nodeId) {
        return [];
    }
    return orderBy(['createdAt'], ['desc'], tags[projectId]?.nodes[nodeId]?.tags);
}

export const selectTagsForNodeAndProjectId = createSelector(
    selectPropProjectId,
    selectPropNodeId,
    selectAllTagsByProjectId,
    getTagsForProjectAndNode
);

export const selectNextTagVersion = createSelector(selectTagsForNodeAndProjectId, (tags) => {
    const currentTag = tags[0];
    return isNumber(currentTag?.version) ? currentTag.version + 1 : 0;
});

export const selectIsLoadingModels = createSelector(selectModels, (models) => models.isLoading);

export const selectModelsData = createSelector(selectModels, (models) => models.data);
export const selectModelsUids = createSelector(selectModelsData, (data) =>
    data.map(({ uid }) => uid)
);
export const selectModelDataForPropNodeId = createSelector(
    selectPropNodeId,
    selectModelsData,
    (nodeId, models) => find({ id: nodeId }, models)
);

export const selectIsLoadingOutputColumns = createSelector(
    selectSlice,
    (slice) => slice.outputColumns.isLoading
);

export const selectColumnsForTagId = createSelector(
    selectPropTagId,
    selectSlice,
    (tagId, slice) =>
        slice.outputColumns.byTagId[tagId]?.map((col) => ({
            label: col.name,
            value: col.name
        })) ?? Defaults.ARRAY
);

export const selectIsDeployingModel = createSelector(selectSlice, get('deployModel.isRunning'));
export const selectDeployModelError = createSelector(selectSlice, get('deployModel.error'));

export const selectModelNodeOutOfSyncError = createSelector(
    selectPropEntityId,
    selectEntitiesById,
    (entityId, entities) => {
        const entity = entities[entityId];
        if (isEmpty(entity) || entity._type !== MODEL_DTO_TYPE || !entity.isOutOfSync) {
            return null;
        }
        const columnsDiff = entity.columnsDiff;
        const isBreakage = !isEmpty(columnsDiff?.removed) || !isEmpty(columnsDiff?.typeChanged);
        return {
            _type: 'errors:ModelOutOfSync',
            level: isBreakage ? ErrorLevel.ERROR : ErrorLevel.WARNING,
            fqnObject: entity.tableFqn
        };
    }
);

const selectFailedModelChecks = createSelector(selectLastJobRun, (jobRun) => {
    const postCheckResults = jobRun?.executionMetadata?.POST_CHECK_RESULTS ?? [];
    return postCheckResults.filter(({ passed }) => !passed);
});

export const selectHasModelFailedChecks = createSelector(
    selectFailedModelChecks,
    (checks) => !isEmpty(checks)
);

export const selectJobLastRunCheckResults = createSelector(
    selectView,
    selectLastJobRun,
    (view, jobRun) => {
        if (isWorkbench() && (isEmpty(view) || view._type !== MODEL_DTO_TYPE)) {
            return [];
        }
        return getRunChecksResults(jobRun);
    }
);

export const selectJobRunPostCheckResults = createSelector(selectJobRun, getRunChecksResults);

export const selectIsLoadingEnvironmentDatabases = createSelector(
    selectSlice,
    get('environmentDatabases.isLoading')
);
export const selectEnvironmentDatabasesError = createSelector(
    selectSlice,
    get('environmentDatabases.error')
);
export const selectEnvironmentDatabases = createSelector(
    selectSlice,
    get('environmentDatabases.data')
);

export const selectIsLoadingEnvironmentSchemas = createSelector(
    selectSlice,
    get('environmentSchemas.isLoading')
);

const selectEnvironmentSchemas = createSelector(selectSlice, get('environmentSchemas.data'));

const selectPropDatabase = (_, props) => props.database;
export const selectEnvironmentSchemasForPropDatabase = createSelector(
    selectPropDatabase,
    selectEnvironmentSchemas,
    (database, schemas) => schemas.filter((schema) => schema.database === database)
);

export const selectIsLoadingEnvironmentRoles = createSelector(
    selectSlice,
    get('environmentRoles.isLoading')
);

export const selectEnvironmentRoles = createSelector(selectSlice, get('environmentRoles.data'));

export const selectEnvironmentRolesError = createSelector(
    selectSlice,
    get('environmentRoles.error')
);
export const selectIsLoadingEnvironmentWarehouses = createSelector(
    selectSlice,
    get('environmentWarehouses.isLoading')
);

export const selectEnvironmentWarehouses = createSelector(
    selectSlice,
    get('environmentWarehouses.data')
);

export const selectIsConvertToModelDialogOpen = createSelector(
    selectSlice,
    get('convertToModelDialog.isOpen')
);

export const selectIsConvertAllDialogOpen = createSelector(selectSlice, get('conversion.isOpen'));

export const selectDeploymentsForConvertAllDialog = createSelector(
    selectSlice,
    get('conversion.deployments')
);

const selectDeploymentId = createSelector(selectSlice, get('convertToModelDialog.entityId'));
export const selectDeploymentToConvert = createSelector(
    selectEntitiesById,
    selectDeploymentId,
    (entitiesById, entityId) => entitiesById?.[entityId]
);

export const selectConversionStatusById = createSelector(selectSlice, get('conversion.statusById'));

export const selectIsRunningConvertToModelCall = createSelector(
    selectConversionStatusById,
    (statusById) => {
        const statuses = Object.values(statusById);
        const hasFailure = statuses.some((status) => status === ConversionStatus.FAILED);

        if (hasFailure) {
            return false;
        }

        return statuses.some((status) => status < ConversionStatus.SUCCEEDED);
    }
);

export const selectNextEntityIdToConvert = createSelector(
    selectConversionStatusById,
    (statusById) => {
        for (const entityId in statusById) {
            if (statusById[entityId] === ConversionStatus.PENDING) {
                return entityId;
            }
            continue;
        }
    }
);
